<template>
  <section
    class="slider-wrapper"
    @mouseenter="stopAutoplay"
    @mouseleave="startAutoplay"
    @touchstart="touchStart"
    @touchend="touchEnd"
  >
    <button
      @click="prevSlide"
      class="prev"
    >
      <svg>
        <use xlink:href="#icon-arrow2"></use>
      </svg>
    </button>
    <div
      v-if="slides"
      class="slides"
    >
      <a
        v-for="(slide, index) in slides"
        :key="`slide-${index}`"
        :class="{'-active': activeindex === index}"
        :href="slide.url"
        :target="slide.target"
      >
        <picture>
          <source
            media="(max-width: 350px)"
            :srcset="slide.media.thumbnails[2].jpg"
          >
          <source
            media="(max-width: 800px)"
            :srcset="slide.media.thumbnails[1].jpg"
          >
          <source
            media="(min-width: 801px)"
            :srcset="slide.media.thumbnails[0].jpg"
          >
          <img :src="slide.media.thumbnails[0].jpg">
        </picture>
      </a>
    </div>
    <button
      @click="nextSlide"
      class="next"
    >
      <svg>
        <use xlink:href="#icon-arrow2"></use>
      </svg>
    </button>
    <div class="pagination">
      <div
        v-for="(slide, index) in slides"
        :key="`page-${index}`"
        class="page"
        @click="setSlide(index)"
        :class="{'-active': activeindex === index}"
      />
    </div>
  </section>
</template>
<script>
import axios from 'axios'

const timerDuration = 5000

export default {
  name: 'HomeSlider',
  mounted() {
    this.getSlides()
  },
  data() {
    return {
      slides: undefined,
      activeindex: undefined,
      intervalID: undefined,
      eventX: undefined,
    }
  },
  computed: {
    api() {
      return this.$store.state.api
    },
  },
  methods: {
    async getSlides() {
      const res = await axios(`${this.api}/sliders`)
      this.slides = res && res.data && res.data.data.find((slider) => slider.label === 'Slider HP').medias
      this.activeindex = 0
      this.startAutoplay()
    },
    startAutoplay() {
      this.stopAutoplay()
      this.intervalID = setInterval(() => {
        this.nextSlide()
      }, timerDuration)
    },
    stopAutoplay() {
      if (!this.intervalID) {
        return
      }
      clearInterval(this.intervalID)
      this.intervalID = undefined
    },
    prevSlide() {
      if (this.activeindex <= 0) {
        this.activeindex = this.slides.length - 1
        return
      }
      this.activeindex -= 1
    },
    nextSlide() {
      if (this.activeindex >= this.slides.length - 1) {
        this.activeindex = 0
        return
      }
      this.activeindex += 1
    },
    setSlide(index) {
      this.activeindex = index;
    },
    eventUnify(ev) {
      return ev.changedTouches ? ev.changedTouches[0] : ev
    },
    touchStart(e) {
      const ev = this.eventUnify(e)
      this.stopAutoplay()
      this.eventX = ev.clientX
    },
    touchEnd(e) {
      const ev = this.eventUnify(e);
      const diff = this.eventX - ev.clientX;

      this.startAutoplay()

      if (!this.isSwipe(diff)) {
        return
      }

      if (diff < 0) {
        return this.nextSlide()
      }
      return this.prevSlide()
    },
    isSwipe(diff) {
      return Math.abs(diff) > 50
    },
  },
}
</script>
<style lang="scss" scoped>

.slider-wrapper {
  height: 164px;
  max-width: 100vw;
  overflow: hidden;
  position: relative;

  @include if-min(350px) {
    height: 280px;
  }

  @include if-min(800px) {
    height: 495px;
  }
}

.slides {
  a {
    pointer-events: none;

    &.-active {
      pointer-events: all;
    }
  }
}

img {
  bottom: -24px;
  max-width: none;
  opacity: 0;
  position: absolute;
  right: 0;
  top: 0;
  transform: translateY(-50%);
  transition: opacity 200ms ease-out;

  @include teq-breakpoint($large) {
    top: 50%;
  }

  @include if-min(1920px) {
    left: 50%;
    right: auto;
    transform: translate(-50%, -50%);
  }

  .-active & {
    opacity: 1;
  }
}

button {
  align-items: center;
  background: none;
  border: none;
  bottom: 0;
  cursor: pointer;
  display: flex;
  justify-content: center;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  transition: opacity 200ms ease-out;
  z-index: 1;

  &:hover {
    opacity: 1;
  }

  &::before {
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  &.prev::before {
    @include teq-breakpoint($large) {
      background: linear-gradient(to right, rgba(black, 0.6), transparent 90%);
    }
  }

  &.next {
    left: auto;
    right: 0;

    &::before {
      @include teq-breakpoint($large) {
        background: linear-gradient(to left, rgba(black, 0.6), transparent 90%);
      }
    }
  }
}

svg {
  fill: black;
  height: 50px;
  width: 30px;

  @include teq-breakpoint($large) {
    fill: white;
  }

  .prev & {
    transform: rotate(0.5turn);
  }
}

.pagination {
  bottom: 90px;
  cursor: pointer;
  display: flex;
  left: 0;
  margin-left: auto;
  margin-right: auto;
  place-content: center;
  position: absolute;
  right: 0;
  z-index: 1;

  @include teq-breakpoint($large) {
    bottom: 20px;
    left: inherit;
    margin-left: inherit;
    margin-right: inherit;
    right: 40px;
  }
}

.page {
  background: $c-boulder;
  border-radius: 50%;
  height: 12px;
  width: 12px;

  &.-active {
    background: $sunglow;
  }

  & + & {
    margin: 0 0 0 10px;
  }
}
</style>
